import { httpClient } from '@/shared/services/http'
import {
  GetUIConfigResponse,
  GetUserModuleConfigResponse,
  LoginCredentials,
  LoginResponse,
  User,
  TenantModule,
  TenantModuleFeature,
  SaveModuleConfigItem,
} from '../types'
import { API_ENDPOINTS, STORAGE_KEYS } from '@/shared/constants'

/**
 * Demo credentials for login
 */
export const DEMO_CREDENTIALS = {
  email: 'demo@example.com',
  password: 'demo123',
} as const

/**
 * Auth API functions
 * Using demo credentials - no actual API calls
 */

export const authApi = {

  /**
   * Get all products
   */
  getUIConfig: async (): Promise<GetUIConfigResponse> => {
    const response = await httpClient.get<GetUIConfigResponse>(API_ENDPOINTS.AUTH.GET_UI_CONFIG)
    return response.data
  },

  /**
   * Login user
   */
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    const response = await httpClient.post<LoginResponse>(API_ENDPOINTS.AUTH.LOGIN, credentials)
    return response.data
  },

  /**
   * Get current user (demo - returns stored user)
   */
  getMe: async (): Promise<User> => {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 300))

    const storedUser = localStorage.getItem(STORAGE_KEYS.USER)
    if (storedUser) {
      return JSON.parse(storedUser)
    }

    // Fallback to API if available
    try {
      const response = await httpClient.get<User>(API_ENDPOINTS.AUTH.ME)
      return response.data
    } catch (error) {
      throw new Error('User not found')
    }
  },

  /**
   * Get module config for top menu based on RBAC user roles
   */
  getUserModuleConfig: async (userId: string | number): Promise<GetUserModuleConfigResponse> => {
    const response = await httpClient.get<GetUserModuleConfigResponse>(
      API_ENDPOINTS.ROLES.MODULE_CONFIG(userId),
    )
    return response.data
  },

  /**
   * Tenant RBAC modules (for navigation builder)
   */
  getTenantModules: async (): Promise<TenantModule[]> => {
    const response = await httpClient.get<any>(API_ENDPOINTS.ROLES.MODULES)
    if (response.data?.data) {
      return response.data.data
    }
    if (Array.isArray(response.data)) {
      return response.data
    }
    return []
  },

  /**
   * Tenant RBAC module features (for navigation builder)
   */
  getTenantModuleFeatures: async (
    moduleId: number | string
  ): Promise<TenantModuleFeature[]> => {
    const response = await httpClient.get<any>(
      API_ENDPOINTS.ROLES.MODULE_FEATURES(moduleId)
    )
    if (response.data?.data) {
      return response.data.data
    }
    if (Array.isArray(response.data)) {
      return response.data
    }
    return []
  },

  /**
   * Save navigation/menu config
   */
  saveModuleConfig: async (payload: SaveModuleConfigItem[]): Promise<void> => {
    await httpClient.post(API_ENDPOINTS.ROLES.MODULE_CONFIG_SAVE, payload)
  },

  /**
   * Check if user exists for forgot password flow
   */
  checkUserExists: async (identifier: string, identifier_type: 'email' | 'mobile') => {
    const response = await httpClient.get(API_ENDPOINTS.AUTH.CHECK_USER, {
      params: {
        identifier,
        identifier_type,
      },
    })
    return response.data
  },

  sendOTP: async (identifier: string, channel: 'EMAIL' | 'SMS', purpose: 'SIGNUP' | 'LOGIN' | 'MFA' | 'PASSWORD_RESET') => {
    const response = await httpClient.post(API_ENDPOINTS.AUTH.SEND_OTP, {
      identifier,
      channel,
      purpose,
    })
    return response.data
  },

  verifyOTP: async (identifier: string, otpCode: string, purpose: 'SIGNUP' | 'LOGIN' | 'MFA' | 'PASSWORD_RESET') => {
    const response = await httpClient.post(API_ENDPOINTS.AUTH.VERIFY_OTP, {
      identifier,
      otpCode,
      purpose,
    })
    return response.data
  },

  resetPassword: async (new_password: string, reset_token: string) => {
    const response = await httpClient.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
      new_password,
      reset_token,
    })
    return response.data
  },

  /**
  * Refresh token
  */
  refreshToken: async (refreshToken: string): Promise<LoginResponse> => {
    const response = await httpClient.post<LoginResponse>(API_ENDPOINTS.AUTH.REFRESH, {
      refresh_token: refreshToken,
    })
    return response.data
  },

  /**
   * Logout user
   */
  logout: async (): Promise<void> => {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 200))
    // Storage is cleared in the hook
  },
}
